// src/pages/bookings/Bookings.js
import React, {
  useEffect,
  useState,
  useCallback,
  useMemo,
  useRef,
} from "react";
import {
  Container,
  Row,
  Col,
  Table,
  Button,
  Modal,
  Form,
  Spinner,
  Alert,
  InputGroup,
  FormControl,
  Pagination,
  Dropdown,
  Nav,
} from "react-bootstrap";
import { debounce } from "lodash";
import api from "../../services/api";
import { formatCurrency } from "../../utils/format";
import { Colors } from "../../constants/Colors";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";

/* ================================================================== */
/* CONSTANTS                                                          */
/* ================================================================== */

const MySwal = withReactContent(Swal);
const ITEMS_PER_PAGE = 10;

/* Google Maps script load coordination */
let isGoogleMapsScriptLoading = false;
let isGoogleMapsScriptLoaded = false;

/* ---------------- Design tokens ---------------- */
const T = {
  radius: 14,
  radiusSm: 10,
  shadow: "0 1px 2px rgba(16,24,40,.04), 0 4px 16px rgba(16,24,40,.06)",
  shadowHover: "0 4px 8px rgba(16,24,40,.06), 0 12px 28px rgba(16,24,40,.10)",
  border: `1px solid ${Colors.BORDER_GRAY}`,
  font: "'Inter', 'Segoe UI', system-ui, sans-serif",
};

/* ---------------- Status metadata ---------------- */
const STATUS_META = {
  Pending: {
    color: Colors.WARNING,
    bg: `${Colors.WARNING}1f`,
    label: "Pending",
  },
  Confirmed: {
    color: Colors.SUCCESS,
    bg: `${Colors.SUCCESS}1f`,
    label: "Accepted",
  },
  Rejected: { color: Colors.RED, bg: `${Colors.RED}1f`, label: "Rejected" },
  Completed: { color: Colors.INFO, bg: `${Colors.INFO}1f`, label: "Completed" },
  Cancelled: {
    color: Colors.TEXT_LIGHT,
    bg: `${Colors.GRAY}26`,
    label: "Cancelled",
  },
};

const STATUS_OPTIONS = [
  { value: "all", label: "All Status" },
  { value: "Pending", label: "Pending" },
  { value: "Confirmed", label: "Accepted" },
  { value: "Rejected", label: "Rejected" },
  { value: "Completed", label: "Completed" },
  { value: "Cancelled", label: "Cancelled" },
];

const PAYMENT_OPTIONS = [
  { value: "all", label: "All Payments" },
  { value: "paid", label: "Paid" },
  { value: "unpaid", label: "Unpaid" },
];

const METHOD_OPTIONS = [
  { value: "all", label: "All Methods" },
  { value: "cash", label: "Cash" },
  { value: "gcash", label: "GCash" },
];

/* ---------------- Stat card definitions ---------------- */
const STAT_CARDS = [
  {
    k: "Pending",
    field: "Pending",
    i: "bi-hourglass-split",
    c: Colors.WARNING,
  },
  {
    k: "Accepted",
    field: "Confirmed",
    i: "bi-check-circle",
    c: Colors.SUCCESS,
  },
  { k: "Rejected", field: "Rejected", i: "bi-x-circle", c: Colors.RED },
  { k: "Completed", field: "Completed", i: "bi-flag", c: Colors.INFO },
  { k: "Paid", field: "Paid", i: "bi-cash-coin", c: Colors.SUCCESS },
  {
    k: "Unpaid",
    field: "Unpaid",
    i: "bi-exclamation-circle",
    c: Colors.WARNING,
  },
];

const EMPTY_COUNTS = {
  Pending: 0,
  Confirmed: 0,
  Rejected: 0,
  Completed: 0,
  Cancelled: 0,
  Paid: 0,
  Unpaid: 0,
  Total: 0,
};

/* ================================================================== */
/* HELPERS                                                            */
/* ================================================================== */

const initials = (first = "", last = "") =>
  `${(first[0] || "").toUpperCase()}${(last[0] || "").toUpperCase()}` || "?";

const isPaidValue = (v) => v === true || v === 1 || v === "1";

const getStatusMeta = (status) =>
  STATUS_META[status] || {
    color: Colors.GRAY,
    bg: `${Colors.GRAY}26`,
    label: status || "Unknown",
  };

const formatDate = (d, t) => {
  if (!d || !t) return "N/A";
  const date = new Date(d);
  const parts = String(t).split(":").map(Number);
  if (parts.length < 2 || parts.some((n) => Number.isNaN(n)))
    return "Invalid Date";
  const [h, m, s = 0] = parts;
  date.setHours(h, m, s);
  if (Number.isNaN(date.getTime())) return "Invalid Date";
  return date.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const computeCountsFromRows = (rows, total) => ({
  Pending: rows.filter((b) => b.status === "Pending").length,
  Confirmed: rows.filter((b) => b.status === "Confirmed").length,
  Rejected: rows.filter((b) => b.status === "Rejected").length,
  Completed: rows.filter((b) => b.status === "Completed").length,
  Cancelled: rows.filter((b) => b.status === "Cancelled").length,
  Paid: rows.filter((b) => isPaidValue(b.is_paid)).length,
  Unpaid: rows.filter((b) => !isPaidValue(b.is_paid)).length,
  Total: total || rows.length,
});

/* ================================================================== */
/* ALERTS                                                             */
/* ================================================================== */

const showLoadingAlert = (title = "Loading...", text = "Please wait") =>
  MySwal.fire({
    title,
    text,
    allowOutsideClick: false,
    didOpen: () => MySwal.showLoading(),
  });

const showSuccessAlert = (title, text) =>
  MySwal.fire({
    title,
    text,
    icon: "success",
    confirmButtonColor: Colors.PRIMARY,
    timer: 2000,
    timerProgressBar: true,
  });

const showErrorAlert = (title, text) =>
  MySwal.fire({
    title,
    text,
    icon: "error",
    confirmButtonColor: Colors.PRIMARY,
  });

/* ================================================================== */
/* ERROR BOUNDARIES                                                   */
/* ================================================================== */

class MapErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <Alert variant="danger">
          <i className="bi bi-exclamation-triangle me-2" />
          Failed to load map. Please refresh or check your API configuration.
        </Alert>
      );
    }
    return this.props.children;
  }
}

class ErrorBoundary extends React.Component {
  state = { hasError: false };
  static getDerivedStateFromError() {
    return { hasError: true };
  }
  render() {
    if (this.state.hasError) {
      return (
        <Container fluid className="py-4">
          <Alert variant="danger">
            <i className="bi bi-exclamation-triangle me-2" />
            An error occurred in the Bookings component. Please refresh.
          </Alert>
        </Container>
      );
    }
    return this.props.children;
  }
}

/* ================================================================== */
/* STYLES                                                             */
/* ================================================================== */

const Styles = () => (
  <style>{`
    /* ---- Page shell ---- */
    .bk-page { font-family: ${T.font}; background: ${Colors.LIGHT_GRAY}; min-height: 100vh; padding: 24px 0 60px; }
    .bk-page .container-fluid { max-width: 1400px; }

    /* ---- Header ---- */
    .bk-header { background: linear-gradient(135deg, ${Colors.GRADIENT_START} 0%, ${Colors.GRADIENT_END} 100%); color: #fff; border-radius: ${T.radius}px; padding: 26px 30px; margin-bottom: 20px; position: relative; overflow: hidden; box-shadow: 0 10px 30px rgba(14,107,248,.22); }
    .bk-header::after { content: ""; position: absolute; top: -60px; right: -60px; width: 220px; height: 220px; background: radial-gradient(circle, rgba(255,255,255,.18) 0%, transparent 70%); border-radius: 50%; }
    .bk-header h2 { font-weight: 700; letter-spacing: -.02em; margin: 0; font-size: 22px; }
    .bk-header p { opacity: .88; margin: 4px 0 0; font-size: 13.5px; }

    /* ---- Tabs ---- */
    .bk-tabs { margin-bottom: 18px; border-bottom: none; gap: 6px; display: flex; background: ${Colors.LIGHT_GRAY}; padding: 6px; border-radius: ${T.radiusSm}; width: fit-content; }
    .bk-tabs .nav-link { border: none; color: ${Colors.TEXT_LIGHT}; font-weight: 600; padding: 9px 16px; border-radius: ${T.radiusSm}; font-size: 13px; transition: all .18s; }
    .bk-tabs .nav-link:hover { background: #fff; color: ${Colors.TEXT_DARK}; }
    .bk-tabs .nav-link.active { background: ${Colors.PRIMARY}; color: #fff; box-shadow: 0 3px 10px ${Colors.PRIMARY}40; }

    /* ---- Stat cards ---- */
    .bk-stat { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 16px 18px; display: flex; align-items: center; gap: 12px; transition: transform .18s ease, box-shadow .18s ease; height: 100%; }
    .bk-stat:hover { transform: translateY(-2px); box-shadow: ${T.shadowHover}; }
    .bk-stat-icon { width: 44px; height: 44px; border-radius: 11px; display: grid; place-items: center; font-size: 19px; flex-shrink: 0; }
    .bk-stat-val { font-size: 21px; font-weight: 700; line-height: 1; color: ${Colors.TEXT_DARK}; }
    .bk-stat-lbl { font-size: 11px; color: ${Colors.TEXT_LIGHT}; text-transform: uppercase; letter-spacing: .05em; margin-top: 4px; font-weight: 600; }

    /* ---- Card ---- */
    .bk-card { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; box-shadow: ${T.shadow}; overflow: hidden; }
    .bk-card-header { padding: 16px 22px; border-bottom: ${T.border}; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 10px; background: #fff; }
    .bk-card-header h5 { margin: 0; font-weight: 600; color: ${Colors.TEXT_DARK}; font-size: 15.5px; display: flex; align-items: center; gap: 8px; }
    .bk-card-header small { font-size: 12.5px; color: ${Colors.TEXT_LIGHT}; }

    /* ---- Toolbar ---- */
    .bk-toolbar { background: #fff; border-radius: ${T.radius}px; border: ${T.border}; padding: 12px; box-shadow: ${T.shadow}; margin-bottom: 18px; }
    .bk-search { border: 1px solid transparent !important; background: ${Colors.LIGHT_GRAY} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; transition: all .18s; height: auto !important; }
    .bk-search:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: 0 0 0 3px ${Colors.PRIMARY}22 !important; }
    .bk-search-icon { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; border-right: none !important; border-radius: ${T.radiusSm} 0 0 ${T.radiusSm} !important; color: ${Colors.GRAY}; }
    .bk-filter-btn { background: ${Colors.LIGHT_GRAY} !important; border: 1px solid transparent !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; padding: 9px 13px !important; font-size: 13.5px; font-weight: 500; text-align: left; display: flex; align-items: center; justify-content: space-between; }
    .bk-filter-btn:hover, .bk-filter-btn:focus { background: #fff !important; border-color: ${Colors.PRIMARY} !important; box-shadow: none !important; color: ${Colors.TEXT_DARK} !important; }
    .bk-filter-btn::after { margin-left: auto; }

    /* ---- Table ---- */
    .bk-table { margin: 0; }
    .bk-table thead th { background: ${Colors.LIGHT_GRAY}; border: none; font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; font-weight: 700; padding: 13px 18px; white-space: nowrap; }
    .bk-table tbody td { padding: 15px 18px; border-color: ${Colors.BORDER_GRAY}; vertical-align: middle; font-size: 13.5px; color: ${Colors.TEXT_DARK}; }
    .bk-table tbody tr { transition: background .15s; }
    .bk-table tbody tr:hover { background: ${Colors.LIGHT_GRAY}55; }

    /* ---- Avatars & chips ---- */
    .bk-avatar { width: 34px; height: 34px; border-radius: 10px; background: linear-gradient(135deg, ${Colors.GRADIENT_START}, ${Colors.GRADIENT_END}); color: #fff; font-weight: 600; font-size: 13px; display: grid; place-items: center; flex-shrink: 0; }
    .bk-id-chip { font-family: ui-monospace, "SF Mono", Menlo, monospace; font-size: 11px; font-weight: 600; background: ${Colors.LIGHT_GRAY}; color: ${Colors.TEXT_LIGHT}; padding: 3px 7px; border-radius: 5px; }

    /* ---- Pills ---- */
    .bk-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 999px; font-size: 11.5px; font-weight: 600; line-height: 1.4; }
    .bk-pill::before { content: ""; width: 6px; height: 6px; border-radius: 50%; background: currentColor; }

    /* ---- Payment method chip ---- */
    .bk-method-chip { display: inline-flex; align-items: center; gap: 5px; padding: 3px 9px; border-radius: 7px; font-size: 11px; font-weight: 700; letter-spacing: .02em; text-transform: uppercase; }
    .bk-method-chip.cash { background: ${Colors.SUCCESS}1f; color: ${Colors.SUCCESS}; }
    .bk-method-chip.gcash { background: #0070E01f; color: #0070E0; }
    .bk-method-chip.unknown { background: ${Colors.GRAY}26; color: ${Colors.TEXT_LIGHT}; }

    /* ---- Refund status pill ---- */
    .bk-refund-pill { display: inline-flex; align-items: center; gap: 6px; padding: 4px 11px; border-radius: 999px; font-size: 11px; font-weight: 700; }
    .bk-refund-pill.pending { background: ${Colors.WARNING}1f; color: ${Colors.WARNING}; border: 1px solid ${Colors.WARNING}50; }
    .bk-refund-pill.refunded { background: ${Colors.SUCCESS}1f; color: ${Colors.SUCCESS}; border: 1px solid ${Colors.SUCCESS}50; }

    /* ---- Empty state ---- */
    .bk-empty { text-align: center; padding: 60px 20px; }
    .bk-empty-icon { width: 80px; height: 80px; margin: 0 auto 18px; border-radius: 50%; background: ${Colors.LIGHT_GRAY}; display: grid; place-items: center; font-size: 34px; color: ${Colors.GRAY}; }
    .bk-empty h5 { font-weight: 600; margin-bottom: 6px; }

    /* ---- Skeleton ---- */
    .bk-skel { background: linear-gradient(90deg, ${Colors.LIGHT_GRAY} 25%, #eee 50%, ${Colors.LIGHT_GRAY} 75%); background-size: 200% 100%; animation: bk-shimmer 1.4s infinite; border-radius: 6px; }
    @keyframes bk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

    /* ---- Buttons ---- */
    .bk-btn-ghost { background: transparent !important; border: 1px solid ${Colors.BORDER_GRAY} !important; color: ${Colors.TEXT_DARK} !important; border-radius: ${T.radiusSm} !important; font-weight: 500; padding: 7px 13px; font-size: 13px; transition: all .18s; }
    .bk-btn-ghost:hover { border-color: ${Colors.PRIMARY} !important; color: ${Colors.PRIMARY} !important; background: ${Colors.PRIMARY}0d !important; }

    /* ---- Info sections ---- */
    .bk-section { background: ${Colors.LIGHT_GRAY}; border-radius: ${T.radiusSm}; padding: 16px; margin-bottom: 14px; }
    .bk-section-title { font-size: 11.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .06em; color: ${Colors.TEXT_LIGHT}; margin-bottom: 12px; display: flex; align-items: center; gap: 7px; }
    .bk-section-title i { color: ${Colors.PRIMARY}; font-size: 14px; }
    .bk-info-row { display: flex; justify-content: space-between; align-items: center; padding: 8px 0; border-bottom: 1px dashed ${Colors.BORDER_GRAY}; font-size: 13px; gap: 12px; }
    .bk-info-row:last-child { border-bottom: none; }
    .bk-info-label { color: ${Colors.TEXT_LIGHT}; flex-shrink: 0; }
    .bk-info-value { color: ${Colors.TEXT_DARK}; font-weight: 600; text-align: right; max-width: 65%; word-break: break-word; }
    
    /* 🔥 Exact Address Highlight */
    .bk-exact-address { 
      background: ${Colors.PRIMARY}0D; 
      color: ${Colors.PRIMARY} !important; 
      padding: 4px 8px; 
      border-radius: 6px; 
      font-size: 12px !important;
      display: inline-block;
      margin-top: 4px;
    }

    /* ---- Modal ---- */
    .modal-content { border-radius: ${T.radius}px; border: none; box-shadow: 0 24px 60px rgba(16,24,40,.18); }
    .modal-header { border-bottom: ${T.border}; padding: 18px 22px; }
    .modal-title { font-weight: 700; color: ${Colors.TEXT_DARK}; font-size: 16px; }
    .modal-body { padding: 20px 22px; }
    .modal-footer { border-top: ${T.border}; padding: 14px 22px; }

    /* ---- Pagination ---- */
    .page-item.active .page-link { background: ${Colors.PRIMARY} !important; border-color: ${Colors.PRIMARY} !important; color: #fff !important; }
    .page-link { color: ${Colors.TEXT_DARK} !important; border-color: ${Colors.BORDER_GRAY} !important; }
    .page-link:hover { background: ${Colors.LIGHT_GRAY} !important; }

    /* ---- Dropdown ---- */
    .dropdown-menu { border-radius: ${T.radiusSm}; border: ${T.border}; box-shadow: ${T.shadow}; padding: 6px; }
    .dropdown-item { border-radius: 6px; padding: 8px 12px; font-size: 13px; }
    .dropdown-item.active { background: ${Colors.PRIMARY}1a !important; color: ${Colors.TEXT_DARK} !important; }
  `}</style>
);

/* ================================================================== */
/* PRESENTATIONAL COMPONENTS                                          */
/* ================================================================== */

const StatusPill = ({ status }) => {
  const meta = getStatusMeta(status);
  return (
    <span
      className="bk-pill"
      style={{ color: meta.color, background: meta.bg }}
    >
      {meta.label}
    </span>
  );
};

const PaymentPill = ({ isPaid }) => {
  const paid = isPaidValue(isPaid);
  const color = paid ? Colors.SUCCESS : Colors.WARNING;
  return (
    <span className="bk-pill" style={{ color, background: `${color}1f` }}>
      {paid ? "Paid" : "Unpaid"}
    </span>
  );
};

const MethodChip = ({ method }) => {
  if (!method)
    return (
      <span className="bk-method-chip unknown">
        <i className="bi bi-question-circle" /> N/A
      </span>
    );
  const isCash = method === "cash";
  return (
    <span className={`bk-method-chip ${isCash ? "cash" : "gcash"}`}>
      <i className={`bi ${isCash ? "bi-cash-stack" : "bi-phone"}`} />
      {isCash ? "Cash" : "GCash"}
    </span>
  );
};

// ✅ NEW: Refund Status Pill
const RefundStatusPill = ({ refundStatus }) => {
  if (!refundStatus || refundStatus === "not_applicable") return null;
  const isPending = refundStatus === "pending";
  return (
    <span className={`bk-refund-pill ${isPending ? "pending" : "refunded"}`}>
      <i
        className={`bi ${isPending ? "bi-hourglass-split" : "bi-check-circle"}`}
      />
      {isPending ? "Refund Pending" : "Refunded"}
    </span>
  );
};

const PersonAvatar = ({ first, last, size = 34, fontSize, gradient }) => (
  <div
    className="bk-avatar"
    style={{
      width: size,
      height: size,
      fontSize: fontSize || size * 0.38,
      background: gradient
        ? `linear-gradient(135deg, ${gradient[0]}, ${gradient[1]})`
        : undefined,
    }}
  >
    {initials(first, last)}
  </div>
);

const RouteLegend = ({ pickup, destination, exactPickup, exactDest }) => (
  <div className="mt-3 d-flex flex-column gap-2">
    <div className="d-flex align-items-start gap-2">
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: Colors.SUCCESS,
          marginTop: 4,
          flexShrink: 0,
        }}
      />
      <div>
        <small className="text-muted d-block">
          <strong style={{ color: Colors.TEXT_DARK }}>Pickup Area:</strong>{" "}
          {pickup}
        </small>
        {!!exactPickup && (
          <small className="bk-exact-address">📍 {exactPickup}</small>
        )}
      </div>
    </div>
    <div className="d-flex align-items-start gap-2">
      <span
        style={{
          width: 10,
          height: 10,
          borderRadius: "50%",
          background: Colors.RED,
          marginTop: 4,
          flexShrink: 0,
        }}
      />
      <div>
        <small className="text-muted d-block">
          <strong style={{ color: Colors.TEXT_DARK }}>Destination Area:</strong>{" "}
          {destination}
        </small>
        {!!exactDest && (
          <small className="bk-exact-address">📍 {exactDest}</small>
        )}
      </div>
    </div>
  </div>
);

/* ================================================================== */
/* MAIN COMPONENT                                                     */
/* ================================================================== */

const Bookings = () => {
  const navigate = useNavigate();
  const abortControllerRef = useRef(null);
  const mapRef = useRef(null);

  // ✅ NEW: Tab state to switch between Bookings, Refunds, and Refunded
  const [activeTab, setActiveTab] = useState("bookings");

  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [paymentFilter, setPaymentFilter] = useState("all");
  const [methodFilter, setMethodFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [updating] = useState(false);
  const [counts, setCounts] = useState(EMPTY_COUNTS);

  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const [map, setMap] = useState(null);
  const [directionsService, setDirectionsService] = useState(null);
  const [directionsRenderer, setDirectionsRenderer] = useState(null);
  const [mapLoading, setMapLoading] = useState(false);
  const [mapDisabled, setMapDisabled] = useState(false);

  // ✅ NEW: Load refunds (filtering our query)
  const fetchBookings = useCallback(async () => {
    abortControllerRef.current?.abort();
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError("");
    try {
      const params = {
        page: currentPage,
        per_page: ITEMS_PER_PAGE,
        search: searchQuery || undefined,
        status:
          activeTab === "refunds" || activeTab === "refunded"
            ? "Cancelled"
            : statusFilter !== "all"
              ? statusFilter
              : undefined,
        is_paid:
          paymentFilter === "paid"
            ? "true"
            : paymentFilter === "unpaid"
              ? "false"
              : undefined,
        preferred_payment_method:
          methodFilter !== "all" ? methodFilter : undefined,
      };

      if (activeTab === "refunds") {
        params.refund_status = "pending";
      } else if (activeTab === "refunded") {
        params.refund_status = "refunded";
      }

      const { data } = await api.get("/admin/rentals", {
        params,
        signal: controller.signal,
      });

      if (data.success && data.data) {
        const rows = data.data.data || [];
        setBookings(rows);
        setTotalPages(data.data.last_page || 1);
        setTotalItems(data.data.total || 0);
        setCounts(
          data.meta?.counts
            ? { ...EMPTY_COUNTS, ...data.meta.counts }
            : computeCountsFromRows(rows, data.data.total),
        );
      } else {
        setBookings([]);
        setTotalPages(1);
        setTotalItems(0);
        setCounts(EMPTY_COUNTS);
      }
    } catch (err) {
      if (err.name === "AbortError" || err.code === "ERR_CANCELED") return;
      if (err.response?.status === 401) {
        localStorage.removeItem("arkila_admin_token");
        navigate("/login");
      } else {
        showErrorAlert("Error", "Failed to load bookings. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }, [
    currentPage,
    searchQuery,
    statusFilter,
    paymentFilter,
    methodFilter,
    activeTab,
    navigate,
  ]);

  const debouncedFetch = useMemo(
    () => debounce(() => fetchBookings(), 300),
    [fetchBookings],
  );

  useEffect(() => () => debouncedFetch.cancel?.(), [debouncedFetch]);

  useEffect(() => {
    const token = localStorage.getItem("arkila_admin_token");
    if (!token) {
      navigate("/login");
      return;
    }
    debouncedFetch();
    return () => {
      debouncedFetch.cancel?.();
      abortControllerRef.current?.abort();
    };
  }, [debouncedFetch, navigate]);

  // Reset filters when switching tabs
  useEffect(() => {
    setStatusFilter("all");
    setPaymentFilter("all");
    setMethodFilter("all");
    setSearchQuery("");
    setCurrentPage(1);
  }, [activeTab]);

  const initializeMap = useCallback(() => {
    if (!window.google?.maps || !mapRef.current) {
      setMapDisabled(true);
      return;
    }
    try {
      const mapInstance = new window.google.maps.Map(mapRef.current, {
        center: { lat: 13.4095, lng: 123.5168 },
        zoom: 12,
        styles: [
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      });
      const ds = new window.google.maps.DirectionsService();
      const dr = new window.google.maps.DirectionsRenderer({
        suppressMarkers: false,
        polylineOptions: {
          strokeColor: Colors.PRIMARY,
          strokeWeight: 4,
          strokeOpacity: 0.85,
        },
      });
      dr.setMap(mapInstance);
      setMap(mapInstance);
      setDirectionsService(ds);
      setDirectionsRenderer(dr);
      setMapDisabled(false);
    } catch (e) {
      setMapDisabled(true);
      showErrorAlert("Map Error", `Failed to initialize: ${e.message}`);
    }
  }, []);

  const loadGoogleMapsScript = useCallback(async () => {
    if (isGoogleMapsScriptLoaded && showModal && mapRef.current) {
      initializeMap();
      return;
    }
    if (isGoogleMapsScriptLoading) return;
    const key = process.env.REACT_APP_GOOGLE_MAPS_API_KEY;
    if (!key) {
      setMapDisabled(true);
      return;
    }
    isGoogleMapsScriptLoading = true;
    try {
      const { Loader } = await import("@googlemaps/js-api-loader");
      const loader = new Loader({ apiKey: key, libraries: ["places"] });
      await loader.load();
      isGoogleMapsScriptLoaded = true;
      isGoogleMapsScriptLoading = false;
      if (showModal && mapRef.current) initializeMap();
    } catch (e) {
      isGoogleMapsScriptLoading = false;
      setMapDisabled(true);
      console.warn("Maps load failed:", e);
    }
  }, [initializeMap, showModal]);

  const displayRoute = useCallback(
    (retry = 0, max = 3) => {
      if (
        mapDisabled ||
        !directionsService ||
        !directionsRenderer ||
        !selectedBooking
      )
        return;
      const {
        pickup_latitude: plat,
        pickup_longitude: plng,
        destination_latitude: dlat,
        destination_longitude: dlng,
      } = selectedBooking;
      if (!plat || !plng || !dlat || !dlng) return;
      setMapLoading(true);
      const request = {
        origin: { lat: parseFloat(plat), lng: parseFloat(plng) },
        destination: { lat: parseFloat(dlat), lng: parseFloat(dlng) },
        travelMode: window.google.maps.TravelMode.DRIVING,
        avoidTolls: true,
      };
      directionsService.route(request, (result, status) => {
        setMapLoading(false);
        if (status === "OK") {
          directionsRenderer.setDirections(result);
        } else if (status === "OVER_QUERY_LIMIT" && retry < max) {
          setTimeout(() => displayRoute(retry + 1, max), 1000);
        } else {
          if (status === "REQUEST_DENIED") {
            console.warn("Directions API denied — check API key restrictions.");
          }
          setMapDisabled(true);
        }
      });
    },
    [directionsService, directionsRenderer, selectedBooking, mapDisabled],
  );

  useEffect(() => {
    if (
      showModal &&
      selectedBooking &&
      !map &&
      !mapDisabled &&
      !isGoogleMapsScriptLoading
    )
      loadGoogleMapsScript();
    if (
      showModal &&
      selectedBooking &&
      directionsService &&
      directionsRenderer &&
      !mapDisabled
    ) {
      const id = setTimeout(displayRoute, 300);
      return () => clearTimeout(id);
    }
  }, [
    showModal,
    selectedBooking,
    directionsService,
    directionsRenderer,
    displayRoute,
    map,
    mapDisabled,
    loadGoogleMapsScript,
  ]);

  useEffect(() => {
    if (!showModal) return undefined;
    const appRoot = document.getElementById("root");
    if (appRoot) appRoot.setAttribute("inert", "");
    return () => appRoot?.removeAttribute("inert");
  }, [showModal]);

  const indexOfLast = currentPage * ITEMS_PER_PAGE;
  const indexOfFirst = indexOfLast - ITEMS_PER_PAGE;
  const paginate = (p) => setCurrentPage(p);

  const renderPaginationItems = () => {
    const items = [];
    const max = 5;
    let start = Math.max(1, currentPage - Math.floor(max / 2));
    const end = Math.min(totalPages, start + max - 1);
    if (end - start + 1 < max) start = Math.max(1, end - max + 1);
    for (let n = start; n <= end; n++) {
      items.push(
        <Pagination.Item
          key={n}
          active={n === currentPage}
          onClick={() => paginate(n)}
        >
          {n}
        </Pagination.Item>,
      );
    }
    return items;
  };

  // ✅ NEW: Handle Refund Processing
  const handleProcessRefund = (booking) => {
    MySwal.fire({
      title: "Process Refund?",
      html: `Are you sure you want to mark rental <b>#${booking.rental_id}</b> as refunded?<br/><small class="text-muted">Ensure you have already sent the money to the passenger's GCash.</small>`,
      input: "text",
      inputPlaceholder: "Enter GCash Ref No. (optional)",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Mark as Refunded",
      confirmButtonColor: Colors.SUCCESS,
      cancelButtonColor: Colors.GRAY,
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          showLoadingAlert("Processing...");
          await api.put(`/admin/rentals/${booking.rental_id}/refund`, {
            refund_reference: result.value || "Manual-Refund",
          });
          showSuccessAlert(
            "Refund Processed",
            `Rental #${booking.rental_id} has been marked as refunded.`,
          );
          fetchBookings();
        } catch (err) {
          showErrorAlert(
            "Error",
            err.response?.data?.message || "Failed to process refund.",
          );
        }
      }
    });
  };

  if (loading && bookings.length === 0) {
    return (
      <div className="bk-page">
        <Styles />
        <Container fluid>
          <div className="bk-header">
            <h2>
              {activeTab === "refunds"
                ? "Refund Management"
                : activeTab === "refunded"
                  ? "Refunded Bookings"
                  : "Bookings Management"}
            </h2>
            <p>Loading your rental requests…</p>
          </div>
          <Row className="g-3 mb-4">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <Col key={i} xs={6} md={4} lg={2}>
                <div className="bk-stat">
                  <div
                    className="bk-skel"
                    style={{ width: 44, height: 44, borderRadius: 11 }}
                  />
                  <div className="flex-grow-1">
                    <div
                      className="bk-skel mb-2"
                      style={{ height: 18, width: "50%" }}
                    />
                    <div
                      className="bk-skel"
                      style={{ height: 11, width: "70%" }}
                    />
                  </div>
                </div>
              </Col>
            ))}
          </Row>
          <div className="bk-card" style={{ padding: 20 }}>
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bk-skel mb-3" style={{ height: 42 }} />
            ))}
          </div>
        </Container>
      </div>
    );
  }

  const safeBookings = Array.isArray(bookings) ? bookings : [];
  const hasActiveFilters =
    searchQuery ||
    statusFilter !== "all" ||
    paymentFilter !== "all" ||
    methodFilter !== "all";

  return (
    <ErrorBoundary>
      <div className="bk-page">
        <Styles />
        <Container fluid>
          <div className="bk-header">
            <div className="position-relative" style={{ zIndex: 1 }}>
              <h2>
                {activeTab === "refunds"
                  ? "Refund Management"
                  : activeTab === "refunded"
                    ? "Refunded Bookings"
                    : "Bookings Management"}
              </h2>
              <p>
                {activeTab === "refunds"
                  ? "Process refunds for operator-cancelled bookings."
                  : activeTab === "refunded"
                    ? "View all successfully processed refunds."
                    : "Manage all rental requests and bookings in one place"}
              </p>
            </div>
          </div>

          {/* ✅ NEW: TABS */}
          <Nav
            variant="pills"
            className="bk-tabs"
            activeKey={activeTab}
            onSelect={(k) => setActiveTab(k)}
          >
            <Nav.Item>
              <Nav.Link eventKey="bookings">
                <i className="bi bi-card-list me-2" /> All Bookings
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="refunds">
                <i className="bi bi-hourglass-split me-2" /> Refunds (Pending)
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey="refunded">
                <i className="bi bi-check-circle me-2" /> Refunded
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError("")}>
              <i className="bi bi-exclamation-triangle me-2" />
              {error}
            </Alert>
          )}
          {success && (
            <Alert variant="success" dismissible onClose={() => setSuccess("")}>
              <i className="bi bi-check-circle me-2" />
              {success}
            </Alert>
          )}

          {/* Only show stat cards on the main bookings tab */}
          {activeTab === "bookings" && (
            <Row className="g-3 mb-4">
              {STAT_CARDS.map((s) => (
                <Col key={s.k} xs={6} md={4} lg={2}>
                  <div className="bk-stat">
                    <div
                      className="bk-stat-icon"
                      style={{ background: `${s.c}1f`, color: s.c }}
                    >
                      <i className={`bi ${s.i}`} />
                    </div>
                    <div>
                      <div className="bk-stat-val">{counts[s.field] ?? 0}</div>
                      <div className="bk-stat-lbl">{s.k}</div>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          )}

          <div className="bk-toolbar">
            <Row className="g-2">
              <Col xs={12} md={6} lg={5}>
                <InputGroup>
                  <InputGroup.Text className="bk-search-icon">
                    <i className="bi bi-search" />
                  </InputGroup.Text>
                  <FormControl
                    className="bk-search"
                    placeholder="Search by passenger, location, or booking ID…"
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </InputGroup>
              </Col>

              {activeTab === "bookings" && (
                <>
                  <Col xs={6} md={3} lg={2}>
                    <Dropdown>
                      <Dropdown.Toggle className="bk-filter-btn w-100">
                        <i className="bi bi-funnel me-2" />
                        {
                          STATUS_OPTIONS.find((o) => o.value === statusFilter)
                            ?.label
                        }
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="w-100">
                        {STATUS_OPTIONS.map((o) => (
                          <Dropdown.Item
                            key={o.value}
                            active={statusFilter === o.value}
                            onClick={() => {
                              setStatusFilter(o.value);
                              setCurrentPage(1);
                            }}
                          >
                            {o.label}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                  <Col xs={6} md={3} lg={2}>
                    <Dropdown>
                      <Dropdown.Toggle className="bk-filter-btn w-100">
                        <i className="bi bi-credit-card me-2" />
                        {
                          PAYMENT_OPTIONS.find((o) => o.value === paymentFilter)
                            ?.label
                        }
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="w-100">
                        {PAYMENT_OPTIONS.map((o) => (
                          <Dropdown.Item
                            key={o.value}
                            active={paymentFilter === o.value}
                            onClick={() => {
                              setPaymentFilter(o.value);
                              setCurrentPage(1);
                            }}
                          >
                            {o.label}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                  <Col xs={12} md={6} lg={3}>
                    <Dropdown>
                      <Dropdown.Toggle className="bk-filter-btn w-100">
                        <i className="bi bi-wallet2 me-2" />
                        {
                          METHOD_OPTIONS.find((o) => o.value === methodFilter)
                            ?.label
                        }
                      </Dropdown.Toggle>
                      <Dropdown.Menu className="w-100">
                        {METHOD_OPTIONS.map((o) => (
                          <Dropdown.Item
                            key={o.value}
                            active={methodFilter === o.value}
                            onClick={() => {
                              setMethodFilter(o.value);
                              setCurrentPage(1);
                            }}
                          >
                            {o.label}
                          </Dropdown.Item>
                        ))}
                      </Dropdown.Menu>
                    </Dropdown>
                  </Col>
                </>
              )}
            </Row>
          </div>

          <div className="bk-card">
            <div className="bk-card-header">
              <h5>
                <i
                  className={`bi ${activeTab === "refunds" ? "bi-cash-stack" : "bi-card-list"}`}
                  style={{ color: Colors.PRIMARY }}
                />
                {activeTab === "refunds"
                  ? "Pending Refunds"
                  : activeTab === "refunded"
                    ? "Refunded Bookings"
                    : "Rental Bookings"}
              </h5>
              <small>
                Showing {Math.min(indexOfFirst + 1, totalItems)}–
                {Math.min(indexOfLast, totalItems)} of {totalItems}
              </small>
            </div>

            {safeBookings.length === 0 ? (
              <div className="bk-empty">
                <div className="bk-empty-icon">
                  <i
                    className={`bi ${activeTab === "refunds" ? "bi-check-circle" : "bi-inbox"}`}
                  />
                </div>
                <h5 style={{ color: Colors.TEXT_DARK }}>
                  {activeTab === "refunds"
                    ? "No pending refunds"
                    : activeTab === "refunded"
                      ? "No refunded bookings"
                      : "No bookings found"}
                </h5>
                <p className="text-muted mb-0">
                  {activeTab === "refunds"
                    ? "All operator cancellations have been refunded."
                    : activeTab === "refunded"
                      ? "Processed refunds will appear here."
                      : hasActiveFilters
                        ? "Try adjusting your search or filter."
                        : "There are no bookings in the system yet."}
                </p>
              </div>
            ) : (
              <>
                <Table responsive hover className="bk-table">
                  <thead>
                    <tr>
                      <th>ID</th>
                      <th>Passenger</th>
                      <th>Operator</th>
                      <th>Jeepney</th>
                      <th>Driver</th>
                      <th>Pax</th>
                      <th>Pick-up Date</th>
                      <th>Location</th>
                      <th>Fare</th>
                      <th>Payment</th>
                      {activeTab === "bookings" && <th>Status</th>}
                      {activeTab === "refunds" && <th>Refund Status</th>}
                      {activeTab === "refunded" && <th>Refund Status</th>}
                      <th style={{ textAlign: "right" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {safeBookings.map((b) => (
                      <tr key={b.rental_id}>
                        <td>
                          <span className="bk-id-chip">#{b.rental_id}</span>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <PersonAvatar
                              first={b.passenger?.first_name}
                              last={b.passenger?.last_name}
                            />
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {b.passenger?.first_name || "Unknown"}{" "}
                                {b.passenger?.last_name || ""}
                              </div>
                              <small className="text-muted">
                                {b.passenger?.email || b.email || "—"}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center gap-2">
                            <PersonAvatar
                              first={b.operator?.first_name}
                              last={b.operator?.last_name}
                              gradient={[Colors.SUCCESS, Colors.INFO]}
                            />
                            <div>
                              <div style={{ fontWeight: 600 }}>
                                {b.operator?.first_name || "—"}{" "}
                                {b.operator?.last_name || ""}
                              </div>
                              <small className="text-muted">
                                {b.operator?.email ||
                                  `Operator #${b.operator_id ?? "—"}`}
                              </small>
                            </div>
                          </div>
                        </td>
                        <td>
                          <div>
                            <div
                              style={{
                                fontWeight: 600,
                                fontFamily:
                                  'ui-monospace, "SF Mono", Menlo, monospace',
                              }}
                            >
                              {b.jeepney?.license_plate || "—"}
                            </div>
                            <small className="text-muted">
                              {b.jeepney?.vehicle_type || "—"}
                            </small>
                          </div>
                        </td>
                        <td>
                          {b.driver ? (
                            <div className="d-flex align-items-center gap-2">
                              <PersonAvatar
                                first={b.driver?.first_name}
                                last={b.driver?.last_name}
                                gradient={[Colors.INFO, Colors.PRIMARY]}
                              />
                              <div>
                                <div style={{ fontWeight: 600 }}>
                                  {b.driver?.first_name} {b.driver?.last_name}
                                </div>
                                <small className="text-muted">
                                  {b.driver?.phone || "—"}
                                </small>
                              </div>
                            </div>
                          ) : (
                            <span
                              className="bk-pill"
                              style={{
                                color: Colors.TEXT_LIGHT,
                                background: `${Colors.GRAY}26`,
                              }}
                            >
                              Unassigned
                            </span>
                          )}
                        </td>
                        <td>
                          <span
                            style={{ fontWeight: 600, color: Colors.TEXT_DARK }}
                          >
                            {b.capacity ?? "—"}
                          </span>
                        </td>
                        <td>
                          <div style={{ fontWeight: 500 }}>
                            {formatDate(b.pickup_date, b.pickup_time)}
                          </div>
                        </td>

                        <td>
                          <div
                            className="d-flex flex-column gap-1"
                            style={{ maxWidth: 250 }}
                          >
                            <div className="d-flex align-items-center gap-2">
                              <i
                                className="bi bi-geo-alt"
                                style={{ color: Colors.PRIMARY }}
                              />
                              <span
                                style={{
                                  overflow: "hidden",
                                  textOverflow: "ellipsis",
                                  whiteSpace: "nowrap",
                                }}
                              >
                                {b.pickup_location || "N/A"}
                              </span>
                            </div>
                            {!!b.pickup_exact_address && (
                              <small
                                className="text-primary"
                                style={{
                                  marginLeft: 20,
                                  fontSize: 11,
                                  fontWeight: 600,
                                }}
                              >
                                📍 {b.pickup_exact_address}
                              </small>
                            )}
                            {!!b.destination_exact_address && (
                              <small
                                className="text-secondary"
                                style={{ marginLeft: 20, fontSize: 11 }}
                              >
                                🏁 {b.destination_exact_address}
                              </small>
                            )}
                          </div>
                        </td>

                        <td
                          style={{ fontWeight: 700, color: Colors.TEXT_DARK }}
                        >
                          {formatCurrency(b.fare)}
                        </td>
                        <td>
                          <PaymentPill isPaid={b.is_paid} />
                        </td>

                        {activeTab === "bookings" && (
                          <td>
                            <StatusPill status={b.status} />
                          </td>
                        )}
                        {activeTab === "refunds" && (
                          <td>
                            <RefundStatusPill refundStatus={b.refund_status} />
                          </td>
                        )}
                        {activeTab === "refunded" && (
                          <td>
                            <RefundStatusPill refundStatus={b.refund_status} />
                          </td>
                        )}

                        <td style={{ textAlign: "right" }}>
                          {activeTab === "refunds" &&
                          b.refund_status === "pending" ? (
                            <Button
                              variant="success"
                              size="sm"
                              onClick={() => handleProcessRefund(b)}
                              style={{ borderRadius: 8, fontWeight: 600 }}
                            >
                              <i className="bi bi-cash-coin me-1" /> Refund
                            </Button>
                          ) : (
                            <Button
                              className="bk-btn-ghost"
                              size="sm"
                              onClick={() => {
                                setSelectedBooking(b);
                                setShowModal(true);
                              }}
                              disabled={updating}
                            >
                              <i className="bi bi-eye me-1" />
                              View
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>

                {totalPages > 1 && (
                  <div
                    className="d-flex justify-content-between align-items-center flex-wrap gap-2"
                    style={{ padding: "14px 20px", borderTop: T.border }}
                  >
                    <small className="text-muted">
                      Page {currentPage} of {totalPages}
                    </small>
                    <Pagination className="mb-0">
                      <Pagination.Prev
                        disabled={currentPage === 1}
                        onClick={() => paginate(currentPage - 1)}
                      />
                      {renderPaginationItems()}
                      <Pagination.Next
                        disabled={currentPage === totalPages}
                        onClick={() => paginate(currentPage + 1)}
                      />
                    </Pagination>
                  </div>
                )}
              </>
            )}
          </div>

          <Modal
            show={showModal}
            onHide={() => !updating && setShowModal(false)}
            size="xl"
            centered
            aria-labelledby="booking-details-modal"
          >
            <Modal.Header closeButton={!updating}>
              <Modal.Title id="booking-details-modal">
                <div className="d-flex align-items-center gap-2">
                  <span
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 10,
                      background: `${Colors.PRIMARY}1f`,
                      color: Colors.PRIMARY,
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <i className="bi bi-receipt" />
                  </span>
                  Booking #{selectedBooking?.rental_id}
                </div>
              </Modal.Title>
            </Modal.Header>

            <Modal.Body>
              <MapErrorBoundary>
                {selectedBooking && (
                  <Row className="g-4">
                    <Col md={6}>
                      <div className="bk-section">
                        <div className="bk-section-title">
                          <i className="bi bi-person" /> Passenger
                        </div>
                        <div className="d-flex align-items-center gap-3">
                          <PersonAvatar
                            first={selectedBooking.passenger?.first_name}
                            last={selectedBooking.passenger?.last_name}
                            size={48}
                            fontSize={16}
                          />
                          <div>
                            <div
                              style={{
                                fontWeight: 700,
                                color: Colors.TEXT_DARK,
                              }}
                            >
                              {selectedBooking.passenger?.first_name ||
                                "Unknown"}{" "}
                              {selectedBooking.passenger?.last_name || ""}
                            </div>
                            <small className="text-muted d-block">
                              {selectedBooking.email ||
                                selectedBooking.passenger?.email ||
                                "No email"}
                            </small>
                            {selectedBooking.phone_number && (
                              <small className="text-muted">
                                {selectedBooking.phone_number}
                              </small>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="bk-section">
                        <div className="bk-section-title">
                          <i className="bi bi-signpost-split" /> Trip Details
                        </div>
                        <div className="bk-info-row">
                          <span className="bk-info-label">Pickup Area</span>
                          <span className="bk-info-value">
                            {selectedBooking.pickup_location || "N/A"}
                          </span>
                        </div>
                        {!!selectedBooking.pickup_exact_address && (
                          <div className="bk-info-row">
                            <span className="bk-info-label">Pickup Exact</span>
                            <span className="bk-info-value bk-exact-address">
                              📍 {selectedBooking.pickup_exact_address}
                            </span>
                          </div>
                        )}
                        <div className="bk-info-row">
                          <span className="bk-info-label">
                            Destination Area
                          </span>
                          <span className="bk-info-value">
                            {selectedBooking.destination || "N/A"}
                          </span>
                        </div>
                        {!!selectedBooking.destination_exact_address && (
                          <div className="bk-info-row">
                            <span className="bk-info-label">Dest. Exact</span>
                            <span className="bk-info-value bk-exact-address">
                              📍 {selectedBooking.destination_exact_address}
                            </span>
                          </div>
                        )}
                        <div className="bk-info-row">
                          <span className="bk-info-label">Purpose</span>
                          <span className="bk-info-value">
                            {selectedBooking.purpose || "N/A"}
                          </span>
                        </div>
                        <div className="bk-info-row">
                          <span className="bk-info-label">Passengers</span>
                          <span className="bk-info-value">
                            {selectedBooking.capacity ?? "—"}
                          </span>
                        </div>
                        <div className="bk-info-row">
                          <span className="bk-info-label">Duration</span>
                          <span className="bk-info-value">
                            {selectedBooking.duration_days
                              ? `${selectedBooking.duration_days} day(s)`
                              : "N/A"}
                          </span>
                        </div>
                        <div className="bk-info-row">
                          <span className="bk-info-label">Est. distance</span>
                          <span className="bk-info-value">
                            {selectedBooking.estimated_distance
                              ? `${selectedBooking.estimated_distance} km`
                              : "N/A"}
                          </span>
                        </div>
                      </div>

                      <div className="bk-section">
                        <div className="bk-section-title">
                          <i className="bi bi-calendar-check" /> Booking Info
                        </div>
                        <div className="bk-info-row">
                          <span className="bk-info-label">Pick-up</span>
                          <span className="bk-info-value">
                            {formatDate(
                              selectedBooking.pickup_date,
                              selectedBooking.pickup_time,
                            )}
                          </span>
                        </div>
                        <div className="bk-info-row">
                          <span className="bk-info-label">Operator</span>
                          <span className="bk-info-value">
                            {selectedBooking.operator
                              ? `${selectedBooking.operator.first_name} ${selectedBooking.operator.last_name}`
                              : "—"}
                          </span>
                        </div>
                        <div className="bk-info-row">
                          <span className="bk-info-label">Jeepney</span>
                          <span className="bk-info-value">
                            {selectedBooking.jeepney?.license_plate || "—"}
                            {selectedBooking.jeepney?.vehicle_type
                              ? ` · ${selectedBooking.jeepney.vehicle_type}`
                              : ""}
                          </span>
                        </div>
                        <div className="bk-info-row">
                          <span className="bk-info-label">Driver</span>
                          <span className="bk-info-value">
                            {selectedBooking.driver
                              ? `${selectedBooking.driver.first_name} ${selectedBooking.driver.last_name}`
                              : "Unassigned"}
                          </span>
                        </div>
                        <div className="bk-info-row">
                          <span className="bk-info-label">Fare</span>
                          <span
                            className="bk-info-value"
                            style={{ color: Colors.PRIMARY, fontSize: 15 }}
                          >
                            {formatCurrency(selectedBooking.fare)}
                          </span>
                        </div>
                        <div className="bk-info-row">
                          <span className="bk-info-label">Payment</span>
                          <PaymentPill isPaid={selectedBooking.is_paid} />
                        </div>
                        <div className="bk-info-row">
                          <span className="bk-info-label">
                            Preferred method
                          </span>
                          <MethodChip
                            method={selectedBooking.preferred_payment_method}
                          />
                        </div>

                        {/* ✅ Show Refund Status if applicable */}
                        {selectedBooking.refund_status &&
                          selectedBooking.refund_status !==
                            "not_applicable" && (
                            <div className="bk-info-row">
                              <span className="bk-info-label">
                                Refund Status
                              </span>
                              <RefundStatusPill
                                refundStatus={selectedBooking.refund_status}
                              />
                            </div>
                          )}
                        {selectedBooking.refund_reference && (
                          <div className="bk-info-row">
                            <span className="bk-info-label">Ref. No.</span>
                            <span className="bk-info-value">
                              {selectedBooking.refund_reference}
                            </span>
                          </div>
                        )}

                        <div className="bk-info-row">
                          <span className="bk-info-label">Status</span>
                          <StatusPill status={selectedBooking.status} />
                        </div>
                        {selectedBooking.notes && (
                          <div className="bk-info-row">
                            <span className="bk-info-label">Notes</span>
                            <span className="bk-info-value">
                              {selectedBooking.notes}
                            </span>
                          </div>
                        )}
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className="bk-section" style={{ height: "100%" }}>
                        <div className="bk-section-title">
                          <i className="bi bi-map" /> Rental Route
                        </div>
                        {mapDisabled ? (
                          <div
                            style={{
                              height: 380,
                              borderRadius: T.radiusSm,
                              background: "#fff",
                              border: `1px dashed ${Colors.BORDER_GRAY}`,
                              display: "grid",
                              placeItems: "center",
                              textAlign: "center",
                              padding: 20,
                            }}
                          >
                            <div>
                              <i
                                className="bi bi-map"
                                style={{ fontSize: 40, color: Colors.GRAY }}
                              />
                              <p
                                className="text-muted mt-2 mb-0"
                                style={{ fontSize: 13 }}
                              >
                                Map unavailable. Please check your Google Maps
                                API key.
                              </p>
                            </div>
                          </div>
                        ) : (
                          <div
                            className="position-relative"
                            style={{
                              borderRadius: T.radiusSm,
                              overflow: "hidden",
                            }}
                          >
                            <div
                              ref={mapRef}
                              style={{
                                height: 380,
                                width: "100%",
                                background: "#fff",
                              }}
                            />
                            {mapLoading && (
                              <div
                                className="position-absolute top-50 start-50 translate-middle"
                                style={{ zIndex: 1000 }}
                              >
                                <Spinner animation="border" variant="primary" />
                              </div>
                            )}
                          </div>
                        )}
                        <RouteLegend
                          pickup={selectedBooking.pickup_location}
                          destination={selectedBooking.destination}
                          exactPickup={selectedBooking.pickup_exact_address}
                          exactDest={selectedBooking.destination_exact_address}
                        />
                      </div>
                    </Col>
                  </Row>
                )}
              </MapErrorBoundary>
            </Modal.Body>

            <Modal.Footer>
              <Button
                className="bk-btn-ghost"
                onClick={() => setShowModal(false)}
                disabled={updating}
              >
                Close
              </Button>
            </Modal.Footer>
          </Modal>
        </Container>
      </div>
    </ErrorBoundary>
  );
};

export default Bookings;
